import React, { useEffect, useRef, useState } from 'react';
import last from 'lodash/last';
import { orderBy } from 'natural-orderby';
import PropTypes from 'prop-types';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import {
    hasMetricSources,
    allMetricSourcesAuthorized,
    areGroupBysDifferent,
    getMetricSourcesForRequests,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import Sparkline from './Sparkline';
import {
    getTimeSeriesTickFormatter,
    getFormattedTimeFromMilliseconds,
    getUniqueArray,
    removeNullOrUndefinedProperties,
} from '../../utils/dataFormatUtils';
import { formatTimeSeriesDataset } from '../../utils/timeSeriesDataFormatUtils';
import { getFormattedValue } from '../../../dashboardUtils';
import { noDataIdentifier } from '../../../../shared/constants/constants';

/* eslint-disable complexity */
function getMetricSources({ metricRequests, metrics }) {
    if (
        metricRequests.length === 0 ||
        metricRequests.length > 2 ||
        metricRequests.some((x) => x.groupBy.length === 0) ||
        metricRequests.some((x) => x.groupBy.length > 2) ||
        metricRequests.some((x) => x.metrics.length !== 1)
    ) {
        return { errorKey: 'invalidMetricRequests' };
    }

    if (areGroupBysDifferent(metricRequests)) {
        return { errorKey: 'metricRequestGroupBysDifferent' };
    }

    const metricSources = getMetricSourcesForRequests(metrics, metricRequests);
    if (metricSources.some((metricSource) => !metricSource)) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized(metricSources)) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources(metricSources)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        metricSources,
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return {
        metricSources,
        metricRequests,
    };
}

export function getDatasetStats(
    filteredData,
    chartProperties,
    threshold,
    dataFormatter
) {
    const latest = last(filteredData);

    const thresholdValue = threshold?.value;

    /* eslint-disable indent */
    return {
        latest: getFormattedValue(
            latest,
            chartProperties.suffix,
            dataFormatter,
            chartProperties.timeFormat
        ),
        // prettier-ignore
        target: thresholdValue
            ? getFormattedValue(
                thresholdValue,
                chartProperties.suffix,
                dataFormatter,
                chartProperties.timeFormat
            )
            : null,
    };
}
/* eslint-enable complexity, indent */

export function getAverage(
    chartProperties,
    dataFormatter,
    dataLabel,
    groupBy,
    metricRequest,
    metricSource
) {
    const currentMetric = metricRequest?.metrics[0];
    const currentMetricSeriesData = metricSource?.metricSeries?.find(
        (x) => x[groupBy]?.toString() === dataLabel
    );

    if (currentMetricSeriesData && currentMetric) {
        return getFormattedValue(
            currentMetricSeriesData[currentMetric],
            chartProperties.suffix,
            dataFormatter,
            chartProperties.timeFormat
        );
    }

    return noDataIdentifier;
}

export function getChartDomain(threshold, metricValues) {
    if (!threshold) {
        return [Math.min(...metricValues), Math.max(...metricValues)];
    }

    return [
        Math.min(...metricValues, threshold.value),
        Math.max(...metricValues, threshold.value),
    ];
}

function processSingleMetric(
    metricRequest,
    metricSource,
    dataLabels,
    numberDecimalFormatter,
    disconnectedGraph
) {
    const metricValueKey = metricRequest.metrics[0];
    const metricNameKey = metricRequest.groupBy[0];
    const formattedData = formatTimeSeriesDataset(
        metricSource.bucketTimes,
        metricSource.metricSeries,
        dataLabels,
        metricNameKey,
        metricValueKey,
        numberDecimalFormatter,
        disconnectedGraph
    );

    return formattedData;
}

function getItemIdDataKeyValue(
    itemIdDataKey,
    dataLabel,
    commonGroupBy,
    metricSources
) {
    if (!itemIdDataKey) {
        return null;
    }

    let itemIdDataKeyValue;

    metricSources.forEach((source) => {
        const datapoint = source.metricSeries.find(
            (point) => point[commonGroupBy] === dataLabel
        );

        if (datapoint?.[itemIdDataKey]) {
            itemIdDataKeyValue = datapoint[itemIdDataKey];
        }
    });

    return itemIdDataKeyValue;
}

function thresholdMatchesItemIdDataKeyValue(
    threshold,
    dataLabel,
    commonGroupBy,
    metricSources
) {
    const dataKeyValue = getItemIdDataKeyValue(
        threshold.itemIdDataKey,
        dataLabel,
        commonGroupBy,
        metricSources
    );

    return threshold.itemId === dataKeyValue;
}

function thresholdMatches(threshold, dataLabel, commonGroupBy, metricSources) {
    if (!threshold.itemId) {
        return false;
    }

    return (
        thresholdMatchesItemIdDataKeyValue(
            threshold,
            dataLabel,
            commonGroupBy,
            metricSources
        ) || threshold.itemId === dataLabel
    );
}

// eslint-disable-next-line complexity
export default function SparklineWrapper(props) {
    const { metricSources, metricRequests, metricSourceErrors, errorKey } =
        getMetricSources(props);
    const wrapperRef = useRef();
    const [activeIndex, setActiveIndex] = useState();
    const [titleWidth, setTitleWidth] = useState(0);

    useEffect(() => {
        if (wrapperRef?.current) {
            const elements = Array.from(
                wrapperRef.current.querySelectorAll(
                    '.selector-container__title'
                )
            );
            const maxWidth = Math.max(...elements.map((el) => el.clientWidth));
            setTitleWidth(maxWidth);
        }
    }, []);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const dataFormatter = props.configuration.isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    const commonGroupBy = metricRequests[0].groupBy[0];

    const dataLabels = getUniqueArray(
        metricSources[0].metricSeries.map((x) => x[commonGroupBy])
    );
    const defaultThreshold = props.thresholds.find((x) => !x.itemId);

    const processedMetricSeries = processSingleMetric(
        metricRequests[0],
        metricSources[0],
        dataLabels,
        props.numberDecimalFormatter,
        props.configuration.disconnectedGraph
    ).map(removeNullOrUndefinedProperties);

    // eslint-disable-next-line complexity
    return (
        <div className="chart-container" ref={wrapperRef}>
            {orderBy(dataLabels).map((dataLabel, index) => {
                const dataValuesWithoutNulls = processedMetricSeries
                    .map((x) => x[dataLabel])
                    .filter((x) => x !== undefined);

                const average = getAverage(
                    props.configuration,
                    dataFormatter,
                    dataLabel,
                    commonGroupBy,
                    metricRequests[1],
                    metricSources[1]
                );

                const threshold =
                    props.thresholds.find((x) =>
                        thresholdMatches(
                            x,
                            dataLabel,
                            commonGroupBy,
                            metricSources
                        )
                    ) ?? defaultThreshold;

                const domain = getChartDomain(
                    threshold,
                    dataValuesWithoutNulls
                );

                const datasetStats = getDatasetStats(
                    dataValuesWithoutNulls,
                    props.configuration,
                    threshold,
                    dataFormatter
                );

                return (
                    <Sparkline
                        key={dataLabel}
                        average={average}
                        chartProperties={props.configuration}
                        dashboardLabels={props.dashboardLabels}
                        dateFormatter={getTimeSeriesTickFormatter(
                            metricRequests[0].timeAndPrecision.precisionType
                        )}
                        dataFormatter={dataFormatter}
                        dataKey={dataLabel}
                        dataPoints={processedMetricSeries}
                        datasetStats={datasetStats}
                        domain={domain}
                        isActive={activeIndex === index}
                        isSelectable={props.isSelectable}
                        onSelectorClick={() => {
                            props.onSelectorClick({
                                [commonGroupBy]: dataLabel,
                            });
                            setActiveIndex(index);
                        }}
                        onSelectorRemoveClick={() => {
                            props.onSelectorRemoveClick();
                            setActiveIndex(undefined);
                        }}
                        threshold={threshold}
                        title={dataLabel}
                        titleWidth={titleWidth}
                    />
                );
            })}
        </div>
    );
}

SparklineWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    isSelectable: PropTypes.bool.isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
    onSelectorClick: PropTypes.func.isRequired,
    onSelectorRemoveClick: PropTypes.func.isRequired,
    thresholds: PropTypes.instanceOf(Array),
};

SparklineWrapper.defaultProps = {
    thresholds: [],
};
